import { useEffect, useState, useRef } from "react";
import api, { API } from "../lib/api";
import { getToken } from "../lib/tokenStorage";
import { toast } from "sonner";
import { Plus, Trash2, ClipboardList, AlertTriangle, BookOpen, Upload, Download, FileText, Youtube, ExternalLink, DollarSign, Pencil, Globe, Landmark } from "lucide-react";
import { useAuth } from "../lib/auth";
import { Link } from "react-router-dom";
import BrandingEditor from "../components/BrandingEditor";
import DOMPurify from "dompurify";

const REGISTRIES = [
  { key: "taxis",    label: "Taxis",    fields: [{k:"name",l:"Taxi / driver name"}, {k:"extra",l:"Car model"}] },
  { key: "hotels",   label: "Hotels / Cities", fields: [{k:"name",l:"City or hotel"}, {k:"extra",l:"Notes"}] },
  { key: "guides",   label: "Guides",   fields: [{k:"name",l:"Guide name"}, {k:"extra",l:"Language(s)"}] },
  // Monuments live in a dedicated collection (`monuments`) that the
  // Weekend Cohort Organizer reads for per-monument entry pricing. Was
  // missing from the Registries UI so PCWM couldn't add / edit them
  // without hitting the API — surfaced here with the four fields the
  // Cohort planner actually consumes.
  { key: "monuments", label: "Monuments", fields: [
      {k:"name",l:"Monument name"},
      {k:"city",l:"City"},
      {k:"weekday_price",l:"Entry price · weekday (INR)",t:"number"},
      {k:"weekend_price",l:"Entry price · weekend (INR)",t:"number"},
      {k:"extra",l:"Notes"},
    ] },
  // Consolidated registry — the earlier separate "Projects" and
  // "Specialisations" registries were folded into Placements per Founder
  // request so PCWM has ONE list to add / rename / delete against.  Rooms
  // moved out of Registries entirely — they live under the dedicated
  // Accommodation page (formerly "Room Management") which handles the
  // room-per-accommodation linkage that a flat registry can't model.
  { key: "placements", label: "Placements", fields: [
      {k:"name",l:"Placement name"},
      {k:"destination",l:"Destination"},
      {k:"city",l:"City"},
      {k:"extra",l:"Notes"},
      {k:"mou_status",l:"MOU status",options:[["","—"],["done","Done · Agreement signed"],["in_process","In process"],["pending","Not started / Pending"]]},
      {k:"mou_expiry_date",l:"MOU expiry date",t:"date"},
    ] },
];

export default function Settings() {
  const { user } = useAuth();
  const [active, setActive] = useState("taxis");
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name:"", extra:"" });
  const [rowEdits, setRowEdits] = useState({}); // id -> {name, extra} pending edit for inline rename
  const [resetOpen, setResetOpen] = useState(false);
  const [resetTyped, setResetTyped] = useState("");
  const [resetting, setResetting] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillReport, setBackfillReport] = useState(null);
  const [loadingDigest, setLoadingDigest] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);
  const previewDigest = async () => {
    setLoadingDigest(true);
    try {
      const { data } = await api.get("/admin/preview-weekly-margin-digest");
      setDigestPreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setLoadingDigest(false); }
  };
  const doBackfillScope = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post("/admin/backfill-location-scope");
      setBackfillReport(data.report);
      const totals = Object.values(data.report || {}).reduce((s, r) => s + (r.backfilled || 0), 0);
      toast.success(`Back-filled ${totals} row(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Back-fill failed"); }
    finally { setBackfilling(false); }
  };

  const startEditRow = (r) => setRowEdits(p => ({ ...p, [r.id]: { name: r.name || "", extra: r.extra || "" } }));
  const cancelEditRow = (id) => setRowEdits(p => { const n = { ...p }; delete n[id]; return n; });
  const saveEditRow = async (id) => {
    const patch = rowEdits[id];
    if (!patch?.name?.trim()) { toast.error("Name required"); return; }
    // Send every configured field — the old shape only forwarded name+extra
    // which silently dropped mou_status / mou_expiry_date on Placements
    // (triggered "Could not rename" toasts because the backend accepted but
    // the UI didn't reflect the change).  Now we include every field the
    // registry config declares.
    const payload = { name: patch.name.trim(), extra: patch.extra || "" };
    cfg.fields.forEach(f => {
      if (f.k === "name" || f.k === "extra") return;
      payload[f.k] = patch[f.k] ?? "";
    });
    try {
      await api.patch(`/${active}/${id}`, payload);
      toast.success("Saved");
      cancelEditRow(id);
      await load(active);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  const doReset = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset-data", { confirm: "RESET AII APP DATA" });
      toast.success(`Reset complete — deleted ${data.total} records`);
      setResetOpen(false); setResetTyped("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
    setResetting(false);
  };

  const cfg = REGISTRIES.find(r => r.key === active);

  const load = async (key) => {
    const { data } = await api.get(`/${key}`);
    setRows(data);
  };
  useEffect(() => { load(active); }, [active]);

  const add = async () => {
    if (!form.name) return toast.error("Name required");
    // Forward every configured field — the old shape only sent whatever
    // sat on `form`, which meant a stale mou_status/mou_expiry_date from
    // a previous session would silently ship.  Building the payload from
    // cfg.fields keeps the POST clean and predictable.
    const payload = {};
    cfg.fields.forEach(f => { payload[f.k] = form[f.k] ?? ""; });
    await api.post(`/${active}`, payload);
    // Reset every configured field so the form is fully clean for the
    // next entry (not just name+extra as before).
    const cleared = {};
    cfg.fields.forEach(f => { cleared[f.k] = ""; });
    setForm(cleared);
    await load(active); toast.success("Added");
  };
  const del = async (id) => { await api.delete(`/${active}/${id}`); await load(active); };

  // Bulk-import a CSV of registry entries.  First column is the name, second
  // column (optional) is the extra field.  Existing entries with the same
  // name are skipped so re-running the same file is safe.  Runs one POST per
  // row sequentially — good enough for PCWM's 50-100 hospital seeds.
  const csvRef = useRef(null);
  const [importing, setImporting] = useState(false);
  // Placements MOU filter — "all" (default) shows every row, "pending"
  // shows in_process / pending / blank, "expiring" shows Done rows whose
  // mou_expiry_date is within the next 30 days.  Filter is inert on any
  // other registry (rooms / taxis / etc) — they don't render a chip bar.
  const [mouFilter, setMouFilter] = useState("all");
  const visibleRows = (() => {
    let list = rows;
    // Placement scoping — a PCWM whose profile carries destination + city
    // only sees placements tagged with the same location (empty = global).
    // Founder / BDOM see everything so they can maintain the master list.
    if (active === "placements" && user?.destination && !["FOUNDER", "BDOM"].includes(user?.role)) {
      const ud = (user.destination || "").toLowerCase();
      const uc = (user.city || "").toLowerCase();
      list = list.filter(r => {
        const pd = (r.destination || "").toLowerCase();
        const pc = (r.city || "").toLowerCase();
        // Global (no destination) OR matching destination + city (city optional).
        return !pd || (pd === ud && (!uc || !pc || pc === uc));
      });
    }
    if (active !== "placements" || mouFilter === "all") return list;
    const today = new Date();
    const in30 = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
    const iso = (d) => d.toISOString().slice(0, 10);
    if (mouFilter === "pending") return list.filter(r => !r.mou_status || r.mou_status === "in_process" || r.mou_status === "pending");
    if (mouFilter === "expiring") return list.filter(r => (r.mou_status === "done") && r.mou_expiry_date && r.mou_expiry_date >= iso(today) && r.mou_expiry_date <= iso(in30));
    return list;
  })();
  const importCsv = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    try {
      const text = await f.text();
      const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      // Detect header row (case-insensitive match on the first field label).
      const headerLabels = cfg.fields.map(x => x.l.toLowerCase());
      const first = (lines[0] || "").split(",").map(s => s.trim().toLowerCase())[0] || "";
      const startIdx = headerLabels.some(h => h.startsWith(first) || first.startsWith(h)) ? 1 : 0;
      const existing = new Set((rows || []).map(r => (r.name || "").trim().toLowerCase()));
      let added = 0, skipped = 0, failed = 0;
      for (const line of lines.slice(startIdx)) {
        // Basic CSV: split on commas, trim quotes.  Names with commas can be
        // wrapped in double quotes — we un-quote here.
        const cells = line.match(/("([^"]|"")*"|[^,]+)/g)?.map(c => c.replace(/^"|"$/g, "").replace(/""/g, '"').trim()) || [];
        const name = (cells[0] || "").trim();
        if (!name) continue;
        if (existing.has(name.toLowerCase())) { skipped++; continue; }
        try {
          await api.post(`/${active}`, { name, extra: cells[1] || "" });
          existing.add(name.toLowerCase());
          added++;
        } catch { failed++; }
      }
      toast.success(`Imported ${added} · skipped ${skipped}${failed ? ` · ${failed} failed` : ""}`);
      await load(active);
    } catch (err) { toast.error(err?.message || "CSV parse failed"); }
    finally { setImporting(false); if (csvRef.current) csvRef.current.value = ""; }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2"><ClipboardList className="text-brand"/><h1 className="text-2xl font-semibold">Registries</h1></div>
      <p className="text-sm text-[color:var(--aii-text-2)]">Master lists used across the app — taxis, hotels, guides, and placements.</p>

      {user?.role === "FOUNDER" && <BrandingEditor />}

      <div className="flex flex-wrap gap-2">
        {REGISTRIES.map(r => (
          <button key={r.key} onClick={()=>setActive(r.key)} className={`px-3 py-1.5 rounded-md text-sm font-semibold ${active===r.key ? "bg-brand text-white" : "bg-white border border-[color:var(--aii-border)] hover:bg-[color:var(--aii-green-tint)]"}`}>{r.label}</button>
        ))}
      </div>

      <div className="aii-card p-5">
        <div className="grid sm:grid-cols-3 gap-3 items-end">
          {cfg.fields.map(f => (
            <div key={f.k}>
              <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">{f.l}</label>
              {f.options ? (
                <select value={form[f.k] || ""}
                        onChange={e=>setForm(v=>({...v, [f.k]: e.target.value}))}
                        data-testid={`reg-${active}-add-${f.k}`}
                        className="input-aii">
                  {f.options.map(([val, label]) => <option key={`opt-${val}`} value={val}>{label}</option>)}
                </select>
              ) : (
                <input value={form[f.k]||""} onChange={e=>setForm(v=>({...v, [f.k]: e.target.value}))} className="input-aii"/>
              )}
            </div>
          ))}
          <button onClick={add} className="bg-brand text-white font-semibold px-4 py-2 rounded-md hover:bg-brand-dark flex items-center gap-1.5"><Plus size={14}/> Add</button>
        </div>
        <div className="mt-3 flex items-center justify-between gap-2 text-xs text-[color:var(--aii-muted)] flex-wrap border-t border-[color:var(--aii-border)] pt-3">
          <div>Or bulk-import a CSV — first column = name, second column = notes (optional). Duplicates are skipped safely.</div>
          <div className="flex items-center gap-2">
            <input ref={csvRef} type="file" accept=".csv,text/csv" className="hidden"
                   data-testid={`reg-${active}-csv-input`} onChange={importCsv}/>
            <button onClick={() => csvRef.current?.click()} disabled={importing}
                    data-testid={`reg-${active}-csv-btn`}
                    className="text-xs px-3 py-1.5 border border-[color:var(--aii-border)] rounded-md hover:bg-[color:var(--aii-green-tint)] font-semibold flex items-center gap-1.5">
              <Upload size={12}/> {importing ? "Importing…" : "Bulk import CSV"}
            </button>
          </div>
        </div>
      </div>

      <div className="aii-card overflow-x-auto">
        {active === "monuments" && <MonumentBulkImport onDone={() => load(active)}/>}
        {active === "placements" && (
          <div className="flex items-center gap-2 p-3 border-b border-[color:var(--aii-border)] text-xs" data-testid="placements-mou-filter">
            <span className="uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold">MOU filter</span>
            {[["all", "All"], ["pending", "Pending / In progress"], ["expiring", "Expiring in 30 days"]].map(([k, label]) => (
              <button key={`mouf-${k}`} data-testid={`placements-mou-${k}`}
                      onClick={() => setMouFilter(k)}
                      className={`px-2.5 py-1 rounded-full font-semibold ${mouFilter === k ? "bg-brand text-white" : "bg-white border border-[color:var(--aii-border)] hover:bg-slate-50"}`}>
                {label}
              </button>
            ))}
            <span className="ml-auto text-[color:var(--aii-muted)]">{visibleRows.length} of {rows.length} placement{rows.length === 1 ? "" : "s"}</span>
          </div>
        )}
        <table className="aii-table">
          <thead><tr>{cfg.fields.map(f => <th key={f.k}>{f.l}</th>)}<th className="text-right">Actions</th></tr></thead>
          <tbody>
            {visibleRows.length===0 && <tr><td colSpan={cfg.fields.length+1} className="text-center py-10 text-[color:var(--aii-muted)]">Empty.</td></tr>}
            {visibleRows.map(r=>{
              const editing = rowEdits[r.id] !== undefined;
              return (
                <tr key={r.id} data-testid={`reg-${active}-row-${r.id}`}>
                  {cfg.fields.map(f => (
                    <td key={f.k}>
                      {editing ? (
                        f.options ? (
                          <select value={rowEdits[r.id][f.k] || ""}
                                  onChange={e => setRowEdits(p => ({ ...p, [r.id]: { ...p[r.id], [f.k]: e.target.value } }))}
                                  className="input-aii py-1 text-sm"
                                  data-testid={`reg-${active}-edit-${f.k}-${r.id}`}>
                            {f.options.map(([val, label]) => <option key={`ropt-${val}`} value={val}>{label}</option>)}
                          </select>
                        ) : (
                          <input type={f.t || "text"} value={rowEdits[r.id][f.k] || ""}
                                 onChange={e => setRowEdits(p => ({ ...p, [r.id]: { ...p[r.id], [f.k]: e.target.value } }))}
                                 className="input-aii py-1 text-sm"
                                 data-testid={`reg-${active}-edit-${f.k}-${r.id}`}/>
                        )
                      ) : (
                        f.options ? (
                          (() => {
                            const cur = r[f.k];
                            const label = (f.options.find(([v]) => v === (cur || ""))?.[1]) || cur || "—";
                            const cls = cur === "done" ? "bg-emerald-100 text-emerald-800"
                                       : cur === "in_process" ? "bg-amber-100 text-amber-800"
                                       : cur === "pending" ? "bg-red-100 text-red-800"
                                       : "text-[color:var(--aii-muted)]";
                            return <span data-testid={`reg-${active}-badge-${f.k}-${r.id}`} className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded ${cls}`}>{label}</span>;
                          })()
                        ) : (r[f.k] || "—")
                      )}
                    </td>
                  ))}
                  <td className="text-right whitespace-nowrap">
                    {editing ? (
                      <>
                        <button onClick={()=>saveEditRow(r.id)} data-testid={`reg-${active}-save-${r.id}`} title="Save"
                                className="text-xs px-2 py-1 bg-brand text-white rounded hover:bg-brand-dark mr-1">Save</button>
                        <button onClick={()=>cancelEditRow(r.id)} title="Cancel"
                                className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded hover:bg-slate-50">Cancel</button>
                      </>
                    ) : (
                      <>
                        <button onClick={()=>startEditRow(r)} data-testid={`reg-${active}-rename-${r.id}`} title="Rename"
                                className="text-[color:var(--aii-muted)] hover:text-brand p-1 mr-1"><Pencil size={14}/></button>
                        <button onClick={()=>del(r.id)} data-testid={`reg-${active}-del-${r.id}`} title="Delete"
                                className="text-[color:var(--aii-muted)] hover:text-red-600 p-1"><Trash2 size={14}/></button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pre-arrival Instructions — BDOM / Founder */}
      {(user.real_role === "FOUNDER" || user.real_role === "BDOM") && (
        <PreArrivalAdmin />
      )}

      {/* Destination content admin — Founder / BDOM / PCWM */}
      {["FOUNDER", "BDOM", "PCWM"].includes(user.real_role) && (
        <Link to="/destinations" data-testid="destinations-link"
          className="aii-card p-5 mt-6 flex items-start gap-4 hover:border-brand hover:shadow-md transition group cursor-pointer">
          <div className="w-12 h-12 rounded-lg bg-[color:var(--aii-green-tint)] text-brand flex items-center justify-center flex-shrink-0">
            <Globe size={22}/>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-semibold text-lg group-hover:text-brand">Destination Content</h3>
            <p className="text-sm text-[color:var(--aii-text-2)] mt-1">Curate the Weather / Currency / Apps / Emergency / Vaccinations / Culture / Landmarks info participants see for each destination city.</p>
          </div>
          <div className="text-brand self-center">
            <ExternalLink size={16}/>
          </div>
        </Link>
      )}

      {/* Data hygiene — Founder only */}
      {user.real_role === "FOUNDER" && (
        <div className="aii-card p-5 mt-6" data-testid="data-hygiene-zone">
          <h3 className="font-semibold flex items-center gap-2"><Globe size={16} className="text-brand"/> Data hygiene</h3>
          <p className="text-sm text-[color:var(--aii-text-2)] mt-2">One-shot back-fill: sweeps legacy <b>threads</b> and <b>service&nbsp;requests</b> that have no destination or city, joins each row to its participant&rsquo;s booking, and stamps the correct office scope. Safe to re-run.</p>
          <button data-testid="backfill-scope-btn" onClick={doBackfillScope} disabled={backfilling}
            className="mt-3 text-sm px-3 py-2 border border-brand text-brand bg-white rounded-md hover:bg-[color:var(--aii-green-tint)] font-semibold disabled:opacity-50">
            {backfilling ? "Back-filling…" : "Back-fill destination + city on legacy rows"}
          </button>
          {backfillReport && (
            <pre data-testid="backfill-scope-report" className="mt-3 text-[11px] bg-[color:var(--aii-bg)] border border-[color:var(--aii-border)] rounded p-2 overflow-x-auto">
{JSON.stringify(backfillReport, null, 2)}
            </pre>
          )}

          {/* Weekly margin digest preview */}
          <div className="mt-5 pt-4 border-t border-[color:var(--aii-border)]">
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold">Weekly digest preview</div>
            <p className="text-sm text-[color:var(--aii-text-2)] mt-1">Rebuild Sunday&rsquo;s cohort-margin digest on demand so you can eyeball what will land in your inbox.</p>
            <button data-testid="preview-margin-digest-btn" onClick={previewDigest} disabled={loadingDigest}
              className="mt-3 text-sm px-3 py-2 border border-brand text-brand bg-white rounded-md hover:bg-[color:var(--aii-green-tint)] font-semibold disabled:opacity-50">
              {loadingDigest ? "Building preview…" : "Preview weekly margin digest"}
            </button>
            {digestPreview && (
              <div className="mt-4 border border-[color:var(--aii-border)] rounded-md p-3 bg-white" data-testid="digest-preview-body">
                <div className="text-[11px] text-[color:var(--aii-muted)] mb-2">{digestPreview.key} · {digestPreview.cohort_count} cohort(s) · {digestPreview.week_start} → {digestPreview.week_end}</div>
                {/* Even though `digestPreview.html` is server-rendered
                    by a Founder-only endpoint, we still sanitize with
                    DOMPurify for defense-in-depth so an accidental
                    upstream regression (or a compromised endpoint)
                    can't inject <script>/<iframe>/on* handlers. */}
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(digestPreview.html || "") }}/>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Danger zone — Founder only */}
      {user.real_role === "FOUNDER" && (
        <div className="aii-card p-5 border-red-300 bg-red-50/50 mt-6" data-testid="danger-zone">
          <h3 className="font-semibold flex items-center gap-2 text-red-700"><AlertTriangle size={16}/> Danger zone</h3>
          <p className="text-sm text-red-800 mt-2">Wipes all user-entered data (bookings, sales, arrivals, C-forms, tasks, notifications, reports, attendance, receipts, housekeeping logs, partners). <b>Keeps</b> team accounts, salaries, default rooms/inventory/WA-templates/registries.</p>
          <button data-testid="reset-btn" onClick={()=>setResetOpen(true)}
            className="mt-3 text-sm px-3 py-2 border border-red-500 text-red-700 bg-white rounded-md hover:bg-red-100 font-semibold">
            Reset app data…
          </button>
        </div>
      )}

      {resetOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full">
            <h3 className="text-lg font-semibold text-red-700 flex items-center gap-2"><AlertTriangle size={18}/> Confirm reset</h3>
            <p className="text-sm text-[color:var(--aii-text-2)] mt-2">This will delete <b>all</b> user-entered data across the whole app. This action cannot be undone.</p>
            <p className="text-sm mt-4">Type <code className="bg-[color:var(--aii-bg)] px-2 py-0.5 rounded text-red-700 font-mono">RESET AII APP DATA</code> below to confirm:</p>
            <input data-testid="reset-confirm-input" autoFocus value={resetTyped} onChange={e=>setResetTyped(e.target.value)}
              className="mt-2 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm font-mono" />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={()=>{ setResetOpen(false); setResetTyped(""); }} className="px-4 py-2 text-sm">Cancel</button>
              <button data-testid="reset-confirm-btn" disabled={resetTyped !== "RESET AII APP DATA" || resetting}
                onClick={doReset}
                className="bg-red-600 text-white font-semibold px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50">
                {resetting ? "Resetting…" : "Yes, wipe all data"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------
// Monument bulk import — Founder/PCWM paste a CSV/TSV with the
// full monument schema (name, city, weekday_price, weekend_price,
// notes). Routes to the dedicated `/monuments/bulk-upload` endpoint
// which round-trips a Google-Sheets export without duplicating rows.
// ------------------------------------------------------------
function MonumentBulkImport({ onDone }) {
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const importCsv = async () => {
    if (!csv.trim()) { toast.error("Paste some CSV first"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/monuments/bulk-upload", { csv });
      setResult(data);
      toast.success(`Created ${data.created} · updated ${data.updated}${data.skipped ? ` · skipped ${data.skipped}` : ""}`);
      setCsv("");
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  const useSample = () => setCsv([
    "name,city,weekday_price,weekend_price,notes",
    "Amber Fort,Jaipur,500,600,Rajasthan heritage",
    "City Palace,Jaipur,200,250,Museum + palace",
    "Hawa Mahal,Jaipur,50,100,Wind palace facade",
  ].join("\n"));

  return (
    <div className="border-b border-[color:var(--aii-border)] p-4 bg-[color:var(--aii-green-tint)]/40" data-testid="monument-bulk-import">
      <div className="flex items-center gap-2 mb-2">
        <Landmark size={16} className="text-brand"/>
        <div>
          <div className="font-semibold text-sm">Bulk import monuments (CSV or TSV)</div>
          <div className="text-[11px] text-[color:var(--aii-muted)]">Header row: <code className="text-[10px] bg-white px-1 rounded">name, city, weekday_price, weekend_price, notes</code> — existing name+city entries are UPDATED so re-running the same file is safe.</div>
        </div>
      </div>
      <textarea
        data-testid="monument-bulk-csv"
        value={csv}
        onChange={e => setCsv(e.target.value)}
        rows={5}
        placeholder="name,city,weekday_price,weekend_price,notes&#10;Taj Mahal,Agra,1100,1500,World heritage"
        className="w-full input-aii text-xs font-mono resize-y"/>
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <button data-testid="monument-bulk-submit" onClick={importCsv} disabled={busy}
          className="bg-brand text-white font-semibold px-3 py-1.5 rounded-md hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1.5 text-xs">
          <Upload size={12}/> {busy ? "Importing…" : "Import monuments"}
        </button>
        <button data-testid="monument-bulk-sample" onClick={useSample} type="button"
          className="text-xs px-3 py-1.5 border border-[color:var(--aii-border)] rounded-md hover:bg-white font-semibold">
          Load sample
        </button>
        {result && (
          <span className="text-[11px] text-[color:var(--aii-muted)]" data-testid="monument-bulk-result">
            Result: <b className="text-emerald-700">{result.created}</b> created · <b className="text-sky-700">{result.updated}</b> updated{result.skipped ? <> · <b>{result.skipped}</b> skipped</> : null}
          </span>
        )}
      </div>
    </div>
  );
}


function PreArrivalAdmin() {
  const [docs, setDocs] = useState([]);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [partners, setPartners] = useState([]);
  const [partnerIds, setPartnerIds] = useState(() => new Set()); // empty = visible to all
  // "file" (default) or "youtube" — controls which input the founder sees
  const [mode, setMode] = useState("file");
  const [ytUrl, setYtUrl] = useState("");
  const fileRef = useRef(null);
  const ACCEPT = ".pdf,.ppt,.pptx,.doc,.docx,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

  const load = async () => {
    try { const { data } = await api.get("/pre-arrival-docs"); setDocs(data || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  };
  useEffect(() => {
    load();
    api.get("/partner-accounts/dropdown").then(({ data }) => setPartners(data || [])).catch(() => {});
  }, []);

  const togglePartner = (pid) => setPartnerIds(prev => {
    const n = new Set(prev);
    if (n.has(pid)) n.delete(pid); else n.add(pid);
    return n;
  });

  const upload = async () => {
    if (!title.trim()) { toast.error("Give it a title"); return; }
    if (mode === "file" && !file) { toast.error("Pick a file"); return; }
    if (mode === "youtube" && !ytUrl.trim()) { toast.error("Paste the YouTube URL"); return; }
    if (mode === "youtube" && !/(youtube\.com|youtu\.be)\//i.test(ytUrl.trim())) {
      toast.error("That doesn't look like a YouTube URL"); return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("title", title.trim());
    if (mode === "file") fd.append("file", file);
    else fd.append("youtube_url", ytUrl.trim());
    if (partnerIds.size > 0) fd.append("partner_ids", [...partnerIds].join(","));
    try {
      await api.post("/pre-arrival-docs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(mode === "youtube" ? "Video link shared · participants notified" : "Uploaded · participants have been notified");
      setTitle(""); setFile(null); setYtUrl(""); setPartnerIds(new Set());
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this document from the participant portal?")) return;
    try { await api.delete(`/pre-arrival-docs/${id}`); toast.success("Removed"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const fmtSize = (b) => b > 1_000_000 ? `${(b/1_000_000).toFixed(1)} MB` : b > 1000 ? `${Math.round(b/1000)} KB` : `${b} B`;
  const iconFor = (d) => {
    if (d?.kind === "youtube") return "▶️";
    const ct = d?.content_type || "";
    if (ct.includes("pdf")) return "📄";
    if (ct.includes("presentation") || ct.includes("powerpoint")) return "📊";
    return "📎";
  };

  return (
    <div className="aii-card p-5 mt-6" data-testid="prearrival-admin">
      <div className="flex items-center gap-2 mb-1">
        <BookOpen size={16} className="text-brand"/>
        <h3 className="font-display font-semibold text-lg">Pre-arrival Instructions</h3>
      </div>
      <p className="text-sm text-[color:var(--aii-text-2)] mb-3">Upload packing lists, cultural orientation decks, medical prep — any PDF, PPT/PPTX or DOC/DOCX up to 15 MB, OR paste a YouTube link. Pick which partner(s) can see it — participants booked with that partner see it in their <i>Pre-arrival</i> tab.</p>

      {/* Partner selector — moved to the top so the founder decides audience
          BEFORE uploading. Empty = every partner sees it (global). */}
      <div className="mb-3">
        <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Show to which partner?</label>
        <div className="flex items-center gap-2 mt-1 flex-wrap" data-testid="prearrival-partner-scope">
          <select
            data-testid="prearrival-partner-dd"
            value={partnerIds.size === 1 ? [...partnerIds][0] : (partnerIds.size === 0 ? "__all__" : "__multi__")}
            onChange={e => {
              const v = e.target.value;
              if (v === "__all__") setPartnerIds(new Set());
              else if (v !== "__multi__") setPartnerIds(new Set([v]));
            }}
            className="input-aii min-w-[240px]">
            <option value="__all__">🌐 All partners (visible to everyone)</option>
            {partnerIds.size > 1 && <option value="__multi__" disabled>{partnerIds.size} partners selected (use chips to change)</option>}
            {partners.map(p => (
              <option key={p.id} value={p.id}>{p.name}{p.country ? ` · ${p.country}` : ""}</option>
            ))}
          </select>
          {partnerIds.size >= 1 && (
            <div className="flex items-center gap-1 flex-wrap">
              {[...partnerIds].map(pid => {
                const p = partners.find(x => x.id === pid);
                if (!p) return null;
                return (
                  <span key={pid} className="text-[11px] px-2 py-0.5 bg-brand text-white rounded-full font-semibold inline-flex items-center gap-1">
                    {p.name}
                    <button type="button" onClick={() => togglePartner(pid)} className="hover:bg-brand-dark rounded-full w-4 h-4 flex items-center justify-center leading-none">×</button>
                  </span>
                );
              })}
              {partners.some(p => !partnerIds.has(p.id)) && (
                <details className="relative">
                  <summary className="text-[11px] px-2 py-0.5 border border-brand text-brand rounded-full font-semibold cursor-pointer list-none">+ add another</summary>
                  <div className="absolute z-10 mt-1 bg-white border border-[color:var(--aii-border)] rounded-md shadow-lg p-2 max-h-56 overflow-y-auto w-64">
                    {partners.filter(p => !partnerIds.has(p.id)).map(p => (
                      <button key={p.id} type="button" onClick={() => togglePartner(p.id)}
                              className="w-full text-left text-xs px-2 py-1 hover:bg-[color:var(--aii-green-tint)] rounded">
                        {p.name}
                      </button>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>
      </div>

      {/* File / YouTube mode toggle */}
      <div className="inline-flex rounded-md border border-[color:var(--aii-border)] overflow-hidden mb-3">
        <button type="button" onClick={() => setMode("file")}
                data-testid="prearrival-mode-file"
                className={`px-3 py-1.5 text-xs font-semibold ${mode === "file" ? "bg-brand text-white" : "bg-white"}`}>
          📎 Upload document
        </button>
        <button type="button" onClick={() => setMode("youtube")}
                data-testid="prearrival-mode-youtube"
                className={`px-3 py-1.5 text-xs font-semibold ${mode === "youtube" ? "bg-brand text-white" : "bg-white"}`}>
          ▶️ YouTube link
        </button>
      </div>

      <div className="grid sm:grid-cols-[1fr_auto_auto] gap-2 items-end">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Document title</label>
          <input data-testid="prearrival-title" value={title} onChange={e=>setTitle(e.target.value)} placeholder={mode === "youtube" ? "e.g. Cultural Orientation — Watch This First" : "e.g. Packing List — India Programme"} className="input-aii w-full"/>
        </div>
        {mode === "file" ? (
          <label className="text-xs px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white hover:bg-[color:var(--aii-green-tint)] cursor-pointer flex items-center gap-2 whitespace-nowrap">
            <Upload size={13} className="text-brand"/> {file ? <span className="truncate max-w-[180px]">{file.name}</span> : "Choose file"}
            <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" data-testid="prearrival-file" onChange={e => setFile(e.target.files?.[0] || null)}/>
          </label>
        ) : (
          <input
            data-testid="prearrival-youtube-url"
            value={ytUrl}
            onChange={e => setYtUrl(e.target.value)}
            placeholder="https://youtube.com/watch?v=..."
            className="input-aii text-sm min-w-[240px]"/>
        )}
        <button data-testid="prearrival-upload" onClick={upload} disabled={busy}
          className="bg-brand text-white font-semibold px-4 py-2 rounded-md hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1.5 text-sm whitespace-nowrap">
          <Upload size={14}/> {busy ? "Saving…" : (mode === "youtube" ? "Share link" : "Upload")}
        </button>
      </div>

      <div className="mt-5">
        {docs.length === 0 ? (
          <div className="text-sm text-[color:var(--aii-muted)] py-6 text-center border border-dashed border-[color:var(--aii-border)] rounded-lg">
            <FileText size={22} className="mx-auto opacity-40 mb-1"/>
            No pre-arrival documents yet — upload your first one above.
          </div>
        ) : (
          <div className="space-y-2">
            {docs.map(d => (
              <div key={d.id} data-testid={`prearrival-row-${d.id}`}
                   className="flex items-center gap-3 p-3 border border-[color:var(--aii-border)] rounded-lg bg-white">
                <span className="text-2xl">{iconFor(d)}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate flex items-center gap-1.5">
                    {d.title}
                    {typeof d.view_count === "number" && d.view_count > 0 && (
                      <span data-testid={`prearrival-views-${d.id}`}
                            title="Unique participants who opened this"
                            className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                        👁 {d.view_count}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-[color:var(--aii-muted)] truncate">
                    {d.kind === "youtube" ? d.youtube_url : `${d.filename} · ${fmtSize(d.size)}`} · by {d.uploaded_by_name}
                    {Array.isArray(d.partner_ids) && d.partner_ids.length > 0 && (
                      <> · <b className="text-brand">Scoped to {d.partner_ids.length} partner{d.partner_ids.length === 1 ? "" : "s"}</b></>
                    )}
                  </div>
                </div>
                <button
                  data-testid={`prearrival-retarget-${d.id}`}
                  title="Change which partners can see this — no re-upload needed"
                  onClick={async () => {
                    const current = new Set(d.partner_ids || []);
                    const input = window.prompt(
                      `Retarget "${d.title}" — type partner numbers separated by commas (empty = global):\n\n` +
                      partners.map((p, i) => `${i + 1}. ${current.has(p.id) ? "✓ " : ""}${p.name}`).join("\n"),
                      [...current].map(pid => partners.findIndex(p => p.id === pid) + 1).filter(n => n > 0).join(",")
                    );
                    if (input === null) return;
                    const nums = input.split(",").map(x => parseInt(x.trim(), 10)).filter(n => n > 0 && n <= partners.length);
                    const newIds = [...new Set(nums.map(n => partners[n - 1].id))];
                    try {
                      await api.patch(`/pre-arrival-docs/${d.id}`, { partner_ids: newIds });
                      toast.success(newIds.length ? `Retargeted to ${newIds.length} partner${newIds.length === 1 ? "" : "s"}` : "Now global (all partners)");
                      await load();
                    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                  }}
                  className="text-xs px-2 py-1 border border-brand text-brand rounded hover:bg-[color:var(--aii-green-tint)] font-semibold whitespace-nowrap">
                  🎯 Retarget
                </button>
                {d.kind === "youtube" ? (
                  <a href={d.youtube_url} target="_blank" rel="noopener noreferrer"
                     data-testid={`prearrival-open-${d.id}`}
                     className="text-xs px-3 py-1.5 border border-brand text-brand rounded-md hover:bg-[color:var(--aii-green-tint)] font-semibold inline-flex items-center gap-1">
                    ▶️ Watch
                  </a>
                ) : (
                <a href={`${API}/files/${d.file_id}?auth=${getToken()}`} target="_blank" rel="noopener noreferrer"
                   data-testid={`prearrival-download-${d.id}`}
                   className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1">
                  <Download size={11}/> Open
                </a>
                )}
                <button onClick={()=>del(d.id)} data-testid={`prearrival-del-${d.id}`}
                        className="text-[color:var(--aii-muted)] hover:text-red-600 p-1.5" title="Remove">
                  <Trash2 size={13}/>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// Shared helpers.
// ------------------------------------------------------------
function ytEmbedThumb(url) {
  const m = (url || "").match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/);
  return m ? `https://img.youtube.com/vi/${m[1]}/mqdefault.jpg` : null;
}

const CURRENCIES = ["USD", "GBP", "EUR", "INR"];
const CUR_SYM = { USD: "$", GBP: "£", EUR: "€", INR: "₹" };

// ------------------------------------------------------------
// Service Registry — Founder/BDOM CRUD service catalog with
// multi-currency pricing. Rows flow onto the Participant Portal
// Services tab + quick-help shortcuts on their Home tab.
// ------------------------------------------------------------
function ServiceRegistryAdmin() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ label: "", price: 0, unit: "per request", free: false, currency: "USD" });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = async () => {
    try { const { data } = await api.get("/service-catalog"); setRows(data || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!f.label.trim()) return toast.error("Service name required");
    setBusy(true);
    try {
      await api.post("/service-catalog", { ...f, price: parseFloat(f.price) || 0 });
      toast.success("Service added"); setF({ label: "", price: 0, unit: "per request", free: false, currency: "USD" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const startEdit = (r) => { setEditing(r.id); setEditForm({ label: r.label, price: r.price, unit: r.unit, free: r.free, currency: r.currency || "USD" }); };
  const saveEdit = async (id) => {
    try { await api.patch(`/service-catalog/${id}`, { ...editForm, price: parseFloat(editForm.price) || 0 }); toast.success("Updated"); setEditing(null); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (r) => { if (!window.confirm(`Remove '${r.label}'?`)) return;
    try { await api.delete(`/service-catalog/${r.id}`); toast.success("Removed"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="aii-card p-5 mt-6" data-testid="service-registry-admin">
      <div className="flex items-center gap-2 mb-1">
        <DollarSign size={16} className="text-brand"/>
        <h3 className="font-display font-semibold text-lg">Service Registry</h3>
      </div>
      <p className="text-sm text-[color:var(--aii-text-2)] mb-4">Add or update services (SIM, transport, taxi, etc.) with prices in USD / GBP / EUR / INR. Every row appears on the participant portal&apos;s <b>Services</b> tab.</p>

      <div className="grid sm:grid-cols-6 gap-2 items-end">
        <input className="input-aii sm:col-span-2" placeholder="Service name *" value={f.label} onChange={e => setF({...f, label: e.target.value})} data-testid="sr-label"/>
        <input className="input-aii" type="number" min="0" placeholder="Price" value={f.price} onChange={e => setF({...f, price: e.target.value})} data-testid="sr-price" disabled={f.free}/>
        <select className="input-aii" value={f.currency} onChange={e => setF({...f, currency: e.target.value})} data-testid="sr-currency">
          {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <input className="input-aii" placeholder="per request / per SIM / etc." value={f.unit} onChange={e => setF({...f, unit: e.target.value})}/>
        <label className="input-aii flex items-center gap-2 text-sm"><input type="checkbox" checked={f.free} onChange={e => setF({...f, free: e.target.checked})}/> Free</label>
      </div>
      <div className="mt-3 flex justify-end">
        <button onClick={submit} disabled={busy} data-testid="sr-submit" className="bg-brand text-white font-semibold px-4 py-2 rounded-md hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1.5"><Plus size={14}/> {busy ? "Adding…" : "Add service"}</button>
      </div>

      <div className="mt-5">
        <table className="aii-table text-sm">
          <thead><tr><th>Service</th><th>Price</th><th>Unit</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-[color:var(--aii-muted)]">No services yet.</td></tr>}
            {rows.map(r => (
              <tr key={r.id} data-testid={`sr-row-${r.id}`}>
                {editing === r.id ? (
                  <>
                    <td><input className="input-aii" value={editForm.label} onChange={e => setEditForm({...editForm, label: e.target.value})}/></td>
                    <td className="flex items-center gap-1">
                      <input className="input-aii w-20" type="number" value={editForm.price} onChange={e => setEditForm({...editForm, price: e.target.value})} disabled={editForm.free}/>
                      <select className="input-aii" value={editForm.currency} onChange={e => setEditForm({...editForm, currency: e.target.value})}>{CURRENCIES.map(c => <option key={c}>{c}</option>)}</select>
                      <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={editForm.free} onChange={e => setEditForm({...editForm, free: e.target.checked})}/> Free</label>
                    </td>
                    <td><input className="input-aii" value={editForm.unit} onChange={e => setEditForm({...editForm, unit: e.target.value})}/></td>
                    <td className="text-right"><button onClick={() => saveEdit(r.id)} className="text-xs px-2 py-1 bg-brand text-white rounded">Save</button><button onClick={() => setEditing(null)} className="text-xs px-2 py-1 ml-1">Cancel</button></td>
                  </>
                ) : (
                  <>
                    <td className="font-medium">{r.label}</td>
                    <td>{r.free ? <span className="text-emerald-700 font-semibold">FREE</span> : `${CUR_SYM[r.currency] || "$"}${r.price}`}</td>
                    <td className="text-xs text-[color:var(--aii-muted)]">{r.unit}</td>
                    <td className="text-right">
                      <button onClick={() => startEdit(r)} className="text-[color:var(--aii-muted)] hover:text-brand p-1"><Pencil size={13}/></button>
                      <button onClick={() => del(r)} className="text-[color:var(--aii-muted)] hover:text-red-600 p-1 ml-1"><Trash2 size={13}/></button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PreArrivalVideosAdmin() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ title: "", youtube_url: "", description: "", target_scope: "all", country: "", city: "", project: "", mandatory: true });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/prearrival-videos"); setRows(data || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!f.title.trim() || !f.youtube_url.trim()) { toast.error("Title + YouTube URL required"); return; }
    if (f.target_scope === "placement" && !f.country && !f.city && !f.project) {
      toast.error("Add at least one of country / city / project when scope is placement"); return;
    }
    setBusy(true);
    try {
      await api.post("/prearrival-videos", f);
      toast.success("Video added · participants notified");
      setF({ title: "", youtube_url: "", description: "", target_scope: "all", country: "", city: "", project: "", mandatory: true });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this video from participants' feed?")) return;
    try { await api.delete(`/prearrival-videos/${id}`); toast.success("Removed"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="aii-card p-5 mt-6" data-testid="prearrival-videos-admin">
      <div className="flex items-center gap-2 mb-1">
        <Youtube size={16} className="text-brand"/>
        <h3 className="font-display font-semibold text-lg">Pre-arrival Videos</h3>
      </div>
      <p className="text-sm text-[color:var(--aii-text-2)] mb-4">
        Paste any YouTube link. Choose <b>All participants</b> or target by <b>Country / City / Project</b> — participants only see videos matching their placement suggestion. Toggle <b>Mandatory</b> to flag must-watch items.
      </p>

      <div className="grid sm:grid-cols-2 gap-2">
        <input className="input-aii" placeholder="Title *" value={f.title} onChange={e => setF({ ...f, title: e.target.value })} data-testid="pv-title"/>
        <input className="input-aii" placeholder="YouTube URL *" value={f.youtube_url} onChange={e => setF({ ...f, youtube_url: e.target.value })} data-testid="pv-yt"/>
        <input className="input-aii sm:col-span-2" placeholder="Short description (optional)" value={f.description} onChange={e => setF({ ...f, description: e.target.value })}/>
        <select className="input-aii" value={f.target_scope} onChange={e => setF({ ...f, target_scope: e.target.value })} data-testid="pv-scope">
          <option value="all">All participants</option>
          <option value="placement">Target by placement</option>
        </select>
        <label className="input-aii flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.mandatory} onChange={e => setF({ ...f, mandatory: e.target.checked })} data-testid="pv-mandatory"/>
          Mandatory to watch
        </label>
        {f.target_scope === "placement" && (
          <>
            <input className="input-aii" placeholder="Country (e.g. India)" value={f.country} onChange={e => setF({ ...f, country: e.target.value })} data-testid="pv-country"/>
            <input className="input-aii" placeholder="City (e.g. Jaipur)" value={f.city} onChange={e => setF({ ...f, city: e.target.value })} data-testid="pv-city"/>
            <input className="input-aii sm:col-span-2" placeholder="Project (e.g. Teaching, Medical & Nursing)" value={f.project} onChange={e => setF({ ...f, project: e.target.value })} data-testid="pv-project"/>
          </>
        )}
      </div>
      <div className="mt-3 flex justify-end">
        <button onClick={submit} disabled={busy} data-testid="pv-submit" className="bg-brand text-white font-semibold px-4 py-2 rounded-md hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1.5">
          <Plus size={14}/> {busy ? "Adding…" : "Add video"}
        </button>
      </div>

      <div className="mt-5">
        {rows.length === 0 ? (
          <div className="text-sm text-[color:var(--aii-muted)] py-6 text-center border border-dashed border-[color:var(--aii-border)] rounded-lg">
            <Youtube size={22} className="mx-auto opacity-40 mb-1"/>
            No pre-arrival videos yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {rows.map(v => (
              <div key={v.id} data-testid={`pv-row-${v.id}`}
                   className="border border-[color:var(--aii-border)] rounded-lg overflow-hidden bg-white flex flex-col">
                <a href={v.youtube_url} target="_blank" rel="noopener noreferrer" className="aspect-video bg-slate-900 block relative">
                  {ytEmbedThumb(v.youtube_url) ? (
                    <img src={ytEmbedThumb(v.youtube_url)} alt="" className="w-full h-full object-cover"/>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Youtube size={40} className="text-white/60"/></div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/25"><Youtube size={40} className="text-white drop-shadow-lg"/></div>
                </a>
                <div className="p-3 flex-1 flex flex-col">
                  <div className="font-semibold text-sm leading-tight flex items-center gap-2">
                    {v.title}
                    {v.mandatory && <span className="text-[9px] uppercase tracking-widest bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Mandatory</span>}
                  </div>
                  <div className="text-[11px] text-[color:var(--aii-muted)] mt-0.5">
                    {v.target_scope === "all" ? "All participants" : (
                      <>Targeted: {[v.country, v.city, v.project].filter(Boolean).join(" · ") || "(none)"}</>
                    )}
                  </div>
                  {v.description && <div className="text-xs text-[color:var(--aii-text-2)] mt-1 line-clamp-2">{v.description}</div>}
                  <div className="mt-3 flex items-center justify-between">
                    <a href={v.youtube_url} target="_blank" rel="noopener noreferrer" className="text-xs text-brand hover:underline flex items-center gap-1"><ExternalLink size={11}/> Open</a>
                    <button onClick={() => del(v.id)} data-testid={`pv-del-${v.id}`} className="text-[color:var(--aii-muted)] hover:text-red-600"><Trash2 size={13}/></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
